/**
 * Exportaciones comerciales del catálogo de listas de precios.
 * Reutiliza el modelo producto.listas_precios + etiquetaLista — sin motor nuevo.
 */
import * as XLSX from 'xlsx';
import { LISTA_PRECIO_KEYS, etiquetaLista } from './listasPreciosLabels';

export function formatCurrencyAr(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(Number(value) || 0);
}

export function buildEmpresaContacto(config = {}) {
  const nombre =
    config.nombre_fantasia ||
    config.razon_social ||
    config.nombre ||
    'Empresa';

  const calle = config.direccion_calle || config.direccion?.calle || '';
  const localidad = config.direccion_localidad || config.direccion?.localidad || '';
  const provincia = config.direccion_provincia || config.direccion?.provincia || '';
  const direccionTexto =
    typeof config.direccion === 'string' && config.direccion.trim()
      ? config.direccion.trim()
      : [calle, localidad, provincia].filter(Boolean).join(', ');

  const telefono =
    config.telefono_principal ||
    config.telefono ||
    config.contacto?.telefono_principal ||
    '';
  const whatsapp =
    config.whatsapp ||
    config.telefono_secundario ||
    config.contacto?.telefono_secundario ||
    telefono ||
    '';
  const email = config.email || config.contacto?.email || '';
  const web = config.website || config.web || config.contacto?.website || '';
  const logo = config.logo_url || config.logo || '';

  const digits = String(whatsapp || '').replace(/\D/g, '');
  const waLink = digits ? `https://wa.me/${digits}` : web || '';
  const qrTarget = waLink || web || (email ? `mailto:${email}` : '');

  return {
    nombre,
    direccion: direccionTexto,
    telefono,
    whatsapp,
    email,
    web,
    logo,
    waLink,
    qrTarget,
    qrImageUrl: qrTarget
      ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(qrTarget)}`
      : ''
  };
}

export function resolveListaPrecio(producto, listaKey) {
  if (!listaKey || listaKey === 'todas') return null;
  const listas = producto?.listas_precios || {};
  const n = parseFloat(listas[listaKey]);
  if (Number.isFinite(n) && n > 0) return n;
  return parseFloat(producto?.precio_venta) || 0;
}

export function groupProductosByCategoria(productos = [], categorias = []) {
  return productos.reduce((acc, producto) => {
    const nombre =
      categorias.find((c) => c.id === producto.categoria_id)?.nombre ||
      producto.categoria ||
      'Sin categoría';
    if (!acc[nombre]) acc[nombre] = [];
    acc[nombre].push(producto);
    return acc;
  }, {});
}

function csvEscape(cell) {
  const raw = cell == null ? '' : String(cell);
  return `"${raw.replace(/"/g, '""')}"`;
}

/**
 * CSV compatible Excel (BOM UTF-8).
 * Columnas: código, producto, categoría, precio, stock opcional.
 */
export function buildCatalogoCsv({
  productos = [],
  categorias = [],
  empresaConfig = null,
  listaKey = 'mayorista',
  incluirStock = false
}) {
  const headers = ['Código', 'Producto', 'Categoría', 'Precio'];
  if (incluirStock) headers.push('Stock');

  const rows = productos.map((p) => {
    const categoria =
      categorias.find((c) => c.id === p.categoria_id)?.nombre ||
      p.categoria ||
      '';
    const precio =
      listaKey === 'todas'
        ? LISTA_PRECIO_KEYS.map((k) => resolveListaPrecio(p, k)).join(' / ')
        : resolveListaPrecio(p, listaKey);
    const row = [
      p.codigo || '',
      p.nombre || '',
      categoria,
      typeof precio === 'number' ? Number(precio).toFixed(2) : precio
    ];
    if (incluirStock) row.push(p.stock_actual ?? 0);
    return row;
  });

  const bom = '\uFEFF';
  const body = [headers, ...rows]
    .map((row) => row.map(csvEscape).join(','))
    .join('\n');

  const listaLabel =
    listaKey === 'todas'
      ? 'todas'
      : etiquetaLista(empresaConfig, listaKey).replace(/\s+/g, '_');

  return {
    content: bom + body,
    filename: `catalogo_${listaLabel}_${new Date().toISOString().slice(0, 10)}.csv`,
    mime: 'text/csv;charset=utf-8;'
  };
}

export function downloadTextFile({ content, filename, mime }) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Excel (.xlsx) — mismas columnas comerciales.
 */
export function exportCatalogoExcel({
  productos = [],
  categorias = [],
  empresaConfig = null,
  listaKey = 'mayorista',
  incluirStock = false
}) {
  const headers = ['Código', 'Producto', 'Categoría', 'Precio'];
  if (incluirStock) headers.push('Stock');

  const data = productos.map((p) => {
    const categoria =
      categorias.find((c) => c.id === p.categoria_id)?.nombre ||
      p.categoria ||
      '';
    const precioNum =
      listaKey === 'todas'
        ? resolveListaPrecio(p, 'mayorista')
        : resolveListaPrecio(p, listaKey);
    const row = {
      Código: p.codigo || '',
      Producto: p.nombre || '',
      Categoría: categoria,
      Precio: Number(precioNum) || 0
    };
    if (incluirStock) row.Stock = p.stock_actual ?? 0;
    if (listaKey === 'todas') {
      LISTA_PRECIO_KEYS.forEach((k) => {
        row[etiquetaLista(empresaConfig, k)] = resolveListaPrecio(p, k);
      });
    }
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(data.length ? data : [{}], {
    header:
      listaKey === 'todas'
        ? [
            'Código',
            'Producto',
            'Categoría',
            ...LISTA_PRECIO_KEYS.map((k) => etiquetaLista(empresaConfig, k)),
            ...(incluirStock ? ['Stock'] : [])
          ]
        : headers
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Catálogo');

  const listaLabel =
    listaKey === 'todas'
      ? 'todas'
      : etiquetaLista(empresaConfig, listaKey).replace(/\s+/g, '_');
  XLSX.writeFile(
    wb,
    `catalogo_${listaLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}

export function uniqueMarcas(productos = []) {
  const set = new Set();
  productos.forEach((p) => {
    const m = String(p.marca || p.marca_nombre || '').trim();
    if (m) set.add(m);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
}
